/* @jsx h */
import { defineComponent, h, ref, computed } from "vue";
import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { ResponsiveTable, RelativeTimeLabel } from "@lemmonhq/uikit";
import type { ResponsiveTableColumn } from "@lemmonhq/uikit";

function StatCard(props: {
  icon: string;
  label: string;
  value: string | number;
  iconBg: string;
  iconColor: string;
  valueColor: string;
}) {
  return (
    <div class="flex items-center gap-4 bg-base-100 rounded-xl border border-base-300/40 px-5 py-4 l-box-shadow">
      <div
        class={`w-11 h-11 rounded-xl ${props.iconBg} flex items-center justify-center shrink-0`}
      >
        <span class={`material-symbols-outlined text-xl ${props.iconColor}`}>
          {props.icon}
        </span>
      </div>
      <div class="min-w-0">
        <p class="text-xs text-base-content/40 uppercase tracking-wider font-medium">
          {props.label}
        </p>
        <p class={`text-2xl font-bold leading-tight ${props.valueColor}`}>
          {props.value}
        </p>
      </div>
    </div>
  );
}
import { useTrpc } from "@app/vue/providers/index";
import { AppSubpage, AppSubpageContent } from "@app/vue/components/AppSubpage";
import { AppSubpageHeader } from "@app/vue/components/AppSubpageHeader";
import type { Campaign } from "@lemmonhq/types";
import { CampaignEditor } from "./CampaignEditor";

// ─── Shared helpers ───────────────────────────────────────────────────────────

const STATUS_CHIP_COLORS: Record<string, string> = {
  draft: "bg-base-content/10 text-base-content/50",
  active: "bg-success/15 text-success",
  archived: "bg-base-content/10 text-base-content/40",
  pending: "bg-warning/15 text-warning",
  running: "bg-info/15 text-info",
  completed: "bg-success/15 text-success",
  failed: "bg-error/15 text-error",
};

function statusChip(status: string) {
  const cls = STATUS_CHIP_COLORS[status] ?? "bg-warning/15 text-warning";
  return (
    <span
      class={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold uppercase ${cls}`}
    >
      {status}
    </span>
  );
}

function channelIcons(channels: string[]) {
  const map: Record<string, string> = {
    email: "mail",
    whatsapp: "chat",
    sms: "textsms",
  };
  return (
    <div class="flex gap-1">
      {channels.map((ch) => (
        <span
          key={ch}
          class="material-symbols-outlined text-base text-base-content/50"
          title={ch}
        >
          {map[ch] ?? "send"}
        </span>
      ))}
    </div>
  );
}

// ─── CampaignsTab ─────────────────────────────────────────────────────────────

const CampaignsTab = defineComponent<{
  onEdit: (campaign: Campaign) => void;
  onRunNow: (campaign: Campaign) => void;
}>({
  name: "CampaignsTab",
  props: ["onEdit", "onRunNow"],
  setup(props) {
    const trpc = useTrpc();
    const searchQuery = ref("");
    const statusFilter = ref<"" | "draft" | "active" | "archived">("");

    const campaignsQuery = useQuery({
      queryKey: computed(() => ["campaigns", "list", statusFilter.value]),
      queryFn: () =>
        trpc.campaigns.list.query({
          status: statusFilter.value || undefined,
          limit: 100,
        }),
      staleTime: 30_000,
    });

    const rows = computed(() => {
      const q = searchQuery.value.toLowerCase();
      return (campaignsQuery.data?.value?.data ?? []).filter((c: Campaign) =>
        q ? c.name.toLowerCase().includes(q) : true,
      );
    });

    const columns: ResponsiveTableColumn[] = [
      {
        key: "name",
        label: "Campaign",
        icon: "campaign",
        mobilePriority: "primary",
        render: (row: Campaign) => (
          <div class="flex flex-col min-w-0">
            <span class="font-medium text-accent truncate">{row.name}</span>
            <div class="flex items-center gap-1.5 mt-0.5">
              {statusChip(row.status)}
              {channelIcons(row.channels)}
            </div>
          </div>
        ),
      },
      {
        key: "targeting",
        label: "Targeting",
        mobilePriority: "secondary",
        render: (row: Campaign) => (
          <span class="text-sm text-base-content/60">
            {row.targeting.type === "explicit"
              ? `${row.targeting.customer_ids.length} customers`
              : `${row.targeting.groups.length} condition group${row.targeting.groups.length > 1 ? "s" : ""}`}
          </span>
        ),
      },
      {
        key: "runs",
        label: "Runs",
        mobilePriority: "secondary",
        render: (row: Campaign) => (
          <span class="text-sm text-base-content/70">{row.total_runs}</span>
        ),
      },
      {
        key: "sent",
        label: "Sent",
        mobilePriority: "secondary",
        render: (row: Campaign) => (
          <span class="text-sm text-base-content/70">
            {row.lifetime_sent.toLocaleString()}
          </span>
        ),
      },
      {
        key: "last_run",
        label: "Last Run",
        mobilePriority: "secondary",
        render: (row: Campaign) =>
          row.last_run_at ? (
            <RelativeTimeLabel
              datetime={row.last_run_at}
              class="text-sm text-base-content/60"
            />
          ) : (
            <span class="text-sm text-base-content/30">Never</span>
          ),
      },
      {
        key: "actions",
        label: "",
        mobilePriority: "actions",
        desktopHide: false,
        render: (row: Campaign) => (
          <div
            class="flex items-center justify-end gap-1"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => props.onRunNow(row)}
              class="btn btn-xs btn-ghost text-primary hover:bg-primary/10"
              title="Run campaign now"
            >
              <span class="material-symbols-outlined text-base">send</span>
              <span class="hidden sm:inline">Run</span>
            </button>
            <button
              type="button"
              onClick={() => props.onEdit(row)}
              class="btn btn-xs btn-ghost hover:bg-base-content/10"
              title="Edit campaign"
            >
              <span class="material-symbols-outlined text-base">edit</span>
            </button>
          </div>
        ),
      },
    ];

    return () => (
      <div class="space-y-5">
        {/* Stats */}
        {campaignsQuery.data?.value &&
          (() => {
            const data = campaignsQuery.data.value.data as Campaign[];
            return (
              <div class="grid grid-cols-3 gap-3">
                <StatCard
                  icon="campaign"
                  label="Total"
                  value={data.length}
                  iconBg="bg-primary/10"
                  iconColor="text-primary"
                  valueColor="text-primary"
                />
                <StatCard
                  icon="check_circle"
                  label="Active"
                  value={data.filter((c) => c.status === "active").length}
                  iconBg="bg-success/10"
                  iconColor="text-success"
                  valueColor="text-success"
                />
                <StatCard
                  icon="send"
                  label="Coupons Sent"
                  value={data
                    .reduce((s, c) => s + c.lifetime_sent, 0)
                    .toLocaleString()}
                  iconBg="bg-info/10"
                  iconColor="text-info"
                  valueColor="text-info"
                />
              </div>
            );
          })()}

        {/* Filters */}
        <div class="flex flex-col sm:flex-row gap-2">
          <div class="relative flex-1">
            <span class="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40 text-base">
              search
            </span>
            <input
              type="text"
              value={searchQuery.value}
              onInput={(e) =>
                (searchQuery.value = (e.target as HTMLInputElement).value)
              }
              placeholder="Search campaigns…"
              class="input input-bordered w-full pl-9 bg-base-200 input-sm"
            />
          </div>
          <select
            value={statusFilter.value}
            onChange={(e) =>
              (statusFilter.value = (e.target as HTMLSelectElement)
                .value as any)
            }
            class="select select-bordered bg-base-200 select-sm"
          >
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="archived">Archived</option>
          </select>
        </div>

        {/* Table */}
        {campaignsQuery.isError.value ? (
          <div class="flex flex-col items-center justify-center py-16 gap-3 text-error">
            <span class="material-symbols-outlined text-4xl">
              error_outline
            </span>
            <p class="text-sm">Failed to load campaigns</p>
            <button
              class="btn btn-sm btn-outline btn-error"
              onClick={() => campaignsQuery.refetch()}
            >
              Retry
            </button>
          </div>
        ) : (
          <ResponsiveTable
            {...{
              columns,
              rows: rows.value,
              rowKey: (row: Campaign) => row.id,
              onRowClick: props.onEdit,
              loading: campaignsQuery.isLoading.value,
              emptyMessage: "No campaigns yet — create your first one",
            }}
          />
        )}
      </div>
    );
  },
});

// ─── RunsTab ──────────────────────────────────────────────────────────────────

const RunsTab = defineComponent({
  name: "RunsTab",
  setup() {
    const trpc = useTrpc();

    const runsQuery = useQuery({
      queryKey: ["campaigns", "runs"],
      queryFn: () => trpc.campaigns.runsList.query({ limit: 100 }),
      staleTime: 15_000,
    });

    const columns: ResponsiveTableColumn[] = [
      {
        key: "campaign",
        label: "Campaign",
        icon: "campaign",
        mobilePriority: "primary",
        render: (row: any) => (
          <div class="flex flex-col min-w-0">
            <span class="font-medium text-accent truncate">
              {row.campaign_name}
            </span>
            <span class="text-xs text-base-content/40 font-mono mt-0.5">
              {row.id.slice(0, 8)}…
            </span>
          </div>
        ),
      },
      {
        key: "status",
        label: "Status",
        mobilePriority: "secondary",
        render: (row: any) => statusChip(row.status),
      },
      {
        key: "progress",
        label: "Progress",
        mobilePriority: "secondary",
        render: (row: any) => {
          const targeted = row.total_targeted ?? 0;
          const sent = row.total_sent ?? 0;
          const failed = row.total_failed ?? 0;
          return (
            <div class="flex items-center gap-1.5 text-sm">
              <span class="text-success">{sent}</span>
              {failed > 0 && <span class="text-error">/ {failed} failed</span>}
              {targeted > 0 && (
                <span class="text-base-content/40">of {targeted}</span>
              )}
            </div>
          );
        },
      },
      {
        key: "triggered_by",
        label: "Triggered",
        mobilePriority: "secondary",
        render: (row: any) => (
          <span class="text-sm text-base-content/60 capitalize">
            {row.triggered_by}
          </span>
        ),
      },
      {
        key: "started_at",
        label: "Started",
        mobilePriority: "secondary",
        render: (row: any) =>
          row.started_at ? (
            <RelativeTimeLabel
              datetime={row.started_at}
              class="text-sm text-base-content/60"
            />
          ) : (
            <span class="text-sm text-base-content/30">—</span>
          ),
      },
      {
        key: "duration",
        label: "Duration",
        mobilePriority: "hidden",
        render: (row: any) => {
          if (!row.started_at || !row.completed_at)
            return <span class="text-base-content/30 text-sm">—</span>;
          const ms =
            new Date(row.completed_at).getTime() -
            new Date(row.started_at).getTime();
          const secs = Math.round(ms / 1000);
          return (
            <span class="text-sm text-base-content/60">
              {secs < 60 ? `${secs}s` : `${Math.round(secs / 60)}m`}
            </span>
          );
        },
      },
    ];

    return () => (
      <div>
        {runsQuery.isError.value ? (
          <div class="flex flex-col items-center justify-center py-16 gap-3 text-error">
            <span class="material-symbols-outlined text-4xl">
              error_outline
            </span>
            <p class="text-sm">Failed to load runs</p>
            <button
              class="btn btn-sm btn-outline btn-error"
              onClick={() => runsQuery.refetch()}
            >
              Retry
            </button>
          </div>
        ) : (
          <ResponsiveTable
            {...{
              columns,
              rows: runsQuery.data?.value ?? [],
              rowKey: (row: any) => row.id,
              loading: runsQuery.isLoading.value,
              emptyMessage: "No runs yet — run a campaign to see history here",
            }}
          />
        )}
      </div>
    );
  },
});

// ─── Page ─────────────────────────────────────────────────────────────────────

export const LoyaltyCampaignsPage = defineComponent({
  name: "LoyaltyCampaignsPage",
  setup() {
    const trpc = useTrpc();
    const queryClient = useQueryClient();
    const activeTab = ref<"campaigns" | "runs">("campaigns");
    const editingCampaign = ref<Campaign | null>(null);
    const showEditor = ref(false);

    const runCreateMutation = useMutation({
      mutationFn: (campaignId: string) =>
        trpc.campaigns.runCreate.mutate({ campaign_id: campaignId }),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["campaigns"] });
        activeTab.value = "runs";
      },
    });

    const handleNewCampaign = () => {
      editingCampaign.value = null;
      showEditor.value = true;
    };

    const handleEdit = (campaign: Campaign) => {
      editingCampaign.value = campaign;
      showEditor.value = true;
    };

    const handleRunNow = (campaign: Campaign) => {
      if (
        !confirm(
          `Start a run for "${campaign.name}"?\n\nThis will generate coupon codes and send messages to all matching customers.`,
        )
      )
        return;
      runCreateMutation.mutate(campaign.id);
    };

    const handleSaved = () => {
      showEditor.value = false;
      editingCampaign.value = null;
    };

    const TABS = [
      { key: "campaigns" as const, label: "Campaigns", icon: "campaign" },
      { key: "runs" as const, label: "Run History", icon: "history" },
    ];

    return () => (
      <div class="flex flex-1 flex-col">
        <AppSubpageHeader
          icon="campaign"
          title="Campaigns"
          description="Targeted marketing campaigns with coupon codes"
        >
          {{
            actions: () => (
              <button
                type="button"
                class="btn btn-primary btn-sm gap-2 shadow-lg hover:shadow-primary/25"
                onClick={handleNewCampaign}
              >
                <span class="material-symbols-outlined text-lg">add</span>
                New Campaign
              </button>
            ),
            bottom: () => (
              <div class="flex gap-0 border-b border-base-content/10 -mb-px">
                {TABS.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => (activeTab.value = tab.key)}
                    class={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                      activeTab.value === tab.key
                        ? "border-primary text-primary"
                        : "border-transparent text-base-content/50 hover:text-base-content/80"
                    }`}
                  >
                    <span class="material-symbols-outlined text-base">
                      {tab.icon}
                    </span>
                    {tab.label}
                  </button>
                ))}
              </div>
            ),
          }}
        </AppSubpageHeader>

        <AppSubpage>
          <AppSubpageContent>
            {activeTab.value === "campaigns" ? (
              <CampaignsTab
                {...{
                  onEdit: handleEdit,
                  onRunNow: handleRunNow,
                }}
              />
            ) : (
              <RunsTab />
            )}
          </AppSubpageContent>

          {showEditor.value && (
            <CampaignEditor
              {...{
                campaign: editingCampaign.value ?? undefined,
                onClose: () => {
                  showEditor.value = false;
                  editingCampaign.value = null;
                },
                onSaved: handleSaved,
              }}
            />
          )}
        </AppSubpage>
      </div>
    );
  },
});
